import { useState, useEffect } from "react";
import { LogoMark } from "@/components/auth/InstitutionSelect";

const DEMO_PASSWORD   = "studoxflow2025";
const DEMO_KEY        = "studox-demo-unlocked-v1";
const DEMO_ROLES      = [
  { role: "student", label: "Schüler",   emoji: "🎓", desc: "Lerne, sammle XP & Coins, schließe Quizze ab" },
  { role: "teacher", label: "Lehrer",    emoji: "📚", desc: "Verwalte Schüler, erstelle Quizze & Aufgaben"  },
  { role: "parent",  label: "Elternteil",emoji: "👨‍👩‍👧", desc: "Verfolge den Fortschritt deines Kindes"        },
  { role: "mentor",  label: "Mentor",    emoji: "🤝", desc: "Betreue Schüler und verfolge Entwicklung"      },
];

// ── Tutorial step definitions per role ────────────────────────────────────────
const TUTORIALS = {
  student: [
    { title: "Willkommen im Dashboard! 🏠", body: "Hier siehst du deine täglichen Quests, Schnellstartoptionen und deinen Fortschritt auf einen Blick.", target: null },
    { title: "XP & Coins ⚡🪙",             body: "Oben rechts siehst du deine aktuellen XP und Coins. Verdiene mehr durch Quizze, Aufgaben und tägliche Quests.", target: null },
    { title: "Quiz starten 📝",             body: "Drücke 'Quiz starten' um Wissen zu testen. Für jede richtige Antwort erhältst du XP und Coins!", target: null },
    { title: "Arena – Multiplayer ⚔️",      body: "In der Arena kannst du gegen andere Schüler in Echtzeit antreten. Erstelle oder betritt einen Raum.", target: null },
    { title: "Badges & Titel 🏷️👑",        body: "Öffne dein Profil → sieh deine Badges und ausgerüsteten Titel. Erfülle Bedingungen um neue zu verdienen.", target: null },
    { title: "Du bist bereit! 🚀",          body: "Das war eine kurze Tour. Erkunde die App auf eigene Faust – es gibt noch viel mehr zu entdecken!", target: null },
  ],
  teacher: [
    { title: "Admin Dashboard 🎛️",         body: "Hier siehst du alle wichtigen Statistiken: Schüleranzahl, aktive Quizze, ausstehende Aufgaben und mehr.", target: null },
    { title: "Schüler verwalten 👥",        body: "Unter 'Schüler' findest du alle Profile. Du kannst XP anpassen, Notizen hinzufügen und Fortschritte einsehen.", target: null },
    { title: "Quiz-Editor 📝",              body: "Erstelle interaktive Quizze mit Fragen, Antwortoptionen und automatischer XP-Vergabe.", target: null },
    { title: "Aufgaben 📌",                 body: "Erstelle IRL- oder digitale Aufgaben. Schüler reichen Beweise ein – du genehmigst oder lehnst ab.", target: null },
    { title: "Gamification 🎮",             body: "Erstelle Badges, Titel und saisonale Events unter 'Gamification'. Werden automatisch an Schüler vergeben.", target: null },
    { title: "Codes verwalten 🔑",          body: "Generiere Einladungscodes für neue Schüler, Lehrer oder Eltern – mit optionaler Institutions-Verknüpfung.", target: null },
  ],
  parent: [
    { title: "Eltern-Dashboard 👨‍👩‍👧",        body: "Hier siehst du den Fortschritt deines Kindes: XP, Coins, Streak und aktuelle Aktivitäten.", target: null },
    { title: "Verknüpfung einrichten 🔗",   body: "Damit du Daten siehst, muss dein Kind zuerst einen Eltern-Einladungscode in den Einstellungen erstellen.", target: null },
    { title: "Nachrichten 💬",             body: "Du kannst direkt mit Lehrern oder Mentoren deines Kindes in Kontakt treten.", target: null },
  ],
  mentor: [
    { title: "Mentor-Dashboard 🤝",        body: "Behalte den Überblick über alle dir zugewiesenen Schüler und deren Fortschritte.", target: null },
    { title: "Schülerprofile 👤",          body: "Öffne ein Schülerprofil um detaillierte Statistiken, Achievements und Aktivitäten zu sehen.", target: null },
    { title: "Nachrichten 💬",            body: "Kommuniziere direkt mit Schülern und Eltern über die integrierte Nachrichtenfunktion.", target: null },
  ],
};

// ─── Tutorial overlay ─────────────────────────────────────────────────────────
function TutorialOverlay({ steps, onDone }) {
  const [step, setStep] = useState(0);
  const current = steps[step];
  const isLast  = step === steps.length - 1;

  return (
    <div className="fixed inset-0 z-[9999] pointer-events-none flex items-end justify-center pb-8 px-4">
      {/* Dark vignette at bottom */}
      <div className="absolute inset-x-0 bottom-0 h-48 pointer-events-none"
        style={{ background: "linear-gradient(transparent, rgba(6,6,26,0.7))" }}/>

      <div
        className="relative w-full max-w-md pointer-events-auto rounded-2xl border border-violet-500/40 shadow-2xl"
        style={{
          background: "linear-gradient(135deg, rgba(19,19,58,0.98) 0%, rgba(13,13,43,0.98) 100%)",
          boxShadow: "0 8px 40px rgba(139,92,246,0.3), 0 0 0 1px rgba(139,92,246,0.2)",
          animation: "nd-in .3s cubic-bezier(.22,.68,0,1.1)",
        }}
      >
        {/* Top accent */}
        <div className="absolute inset-x-0 top-0 h-px rounded-t-2xl"
          style={{ background: "linear-gradient(90deg,transparent,rgba(139,92,246,0.8),transparent)" }}/>

        <div className="p-5">
          {/* Step indicator */}
          <div className="flex items-center justify-between mb-3">
            <div className="flex gap-1.5">
              {steps.map((_, i) => (
                <div key={i}
                  className="rounded-full transition-all duration-300"
                  style={{
                    width: i === step ? 20 : 6,
                    height: 6,
                    background: i === step ? "#8b5cf6" : i < step ? "#8b5cf680" : "rgba(255,255,255,0.1)",
                  }}
                />
              ))}
            </div>
            <span className="text-[10px] text-gray-500 font-bold">{step + 1} / {steps.length}</span>
          </div>

          <h3 className="font-black text-base mb-2">{current.title}</h3>
          <p className="text-sm text-gray-400 leading-relaxed">{current.body}</p>

          <div className="flex gap-2 mt-4">
            {step > 0 && (
              <button onClick={() => setStep((s) => s - 1)}
                className="px-4 py-2 rounded-xl text-xs font-bold border border-nexus-border text-gray-400 hover:text-white transition-all">
                Zurück
              </button>
            )}
            <button
              onClick={() => isLast ? onDone() : setStep((s) => s + 1)}
              className="flex-1 py-2 rounded-xl text-sm font-black bg-gradient-to-r from-violet-600 to-cyan-600 text-white hover:brightness-110 transition-all"
            >
              {isLast ? "Tour beenden 🎉" : "Weiter →"}
            </button>
            {!isLast && (
              <button onClick={onDone}
                className="px-4 py-2 rounded-xl text-xs font-bold text-gray-600 hover:text-gray-400 transition-all">
                Überspringen
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Password gate ────────────────────────────────────────────────────────────
function PasswordGate({ onUnlock }) {
  const [password, setPassword]   = useState("");
  const [error, setError]         = useState(false);
  const [shaking, setShaking]     = useState(false);

  function submit(e) {
    e.preventDefault();
    if (password.toLowerCase().trim() === DEMO_PASSWORD) {
      sessionStorage.setItem(DEMO_KEY, "1");
      onUnlock();
    } else {
      setError(true);
      setShaking(true);
      setTimeout(() => setShaking(false), 500);
    }
  }

  return (
    <div className="min-h-dvh bg-nexus-bg flex items-center justify-center px-4" style={{
      background: "radial-gradient(ellipse at 50% 30%, rgba(139,92,246,0.12) 0%, transparent 60%), #06061a",
    }}>
      <div
        className={`w-full max-w-sm ${shaking ? "animate-[shake_0.4s_ease-in-out]" : ""}`}
        style={{ animation: shaking ? "shake .4s ease" : undefined }}
      >
        <div className="text-center mb-8">
          <div className="flex justify-center mb-5">
            <LogoMark size={64} />
          </div>
          <h1 className="text-2xl font-black mb-1">Demo-Zugang</h1>
          <p className="text-sm text-gray-500">
            Gib das Demo-Passwort ein um die App zu erkunden
          </p>
        </div>

        <form onSubmit={submit} className="space-y-3">
          <div>
            <input
              type="password"
              value={password}
              onChange={(e) => { setPassword(e.target.value); setError(false); }}
              placeholder="Demo-Passwort"
              autoFocus
              className={`w-full px-4 py-3.5 rounded-2xl border text-sm font-bold focus:outline-none transition-all duration-200 bg-nexus-s2 text-white placeholder-gray-600 ${
                error
                  ? "border-red-500/60 focus:border-red-500"
                  : "border-nexus-border focus:border-violet-500/60"
              }`}
              style={{ boxShadow: error ? "0 0 0 3px rgba(239,68,68,0.12)" : undefined }}
            />
            {error && (
              <p className="text-xs text-red-400 mt-2 flex items-center gap-1">
                <span>❌</span> Falsches Passwort. Versuch es nochmal.
              </p>
            )}
          </div>

          <button
            type="submit"
            className="w-full py-3.5 rounded-2xl font-black text-sm bg-gradient-to-r from-violet-600 to-cyan-600 text-white hover:brightness-110 active:scale-[0.98] transition-all"
            style={{ boxShadow: "0 4px 20px rgba(139,92,246,0.4)" }}
          >
            Demo starten →
          </button>
        </form>

        <p className="text-center text-xs text-gray-700 mt-6">
          StudoX Flow — interaktive Demo
        </p>
      </div>
    </div>
  );
}

// ─── Role selector ────────────────────────────────────────────────────────────
function RoleSelector({ onSelect }) {
  return (
    <div className="min-h-dvh bg-nexus-bg flex items-center justify-center px-4" style={{
      background: "radial-gradient(ellipse at 50% 20%, rgba(139,92,246,0.1) 0%, transparent 55%), #06061a",
    }}>
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <div className="flex justify-center mb-5">
            <LogoMark size={64} />
          </div>
          <h1 className="text-2xl font-black mb-1">Rolle wählen</h1>
          <p className="text-sm text-gray-500">Welche Perspektive möchtest du erkunden?</p>
        </div>

        <div className="space-y-2">
          {DEMO_ROLES.map((r) => (
            <button
              key={r.role}
              onClick={() => onSelect(r.role)}
              className="w-full flex items-center gap-4 p-4 rounded-2xl border border-nexus-border hover:border-violet-500/40 bg-nexus-s2 hover:bg-nexus-s3 transition-all duration-200 group hover:scale-[1.01] active:scale-[0.99] text-left"
            >
              <span className="text-3xl">{r.emoji}</span>
              <div>
                <p className="font-black text-sm">{r.label}</p>
                <p className="text-[10px] text-gray-500 mt-0.5">{r.desc}</p>
              </div>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" strokeWidth="2.5" stroke="currentColor"
                strokeLinecap="round" strokeLinejoin="round"
                className="ml-auto text-gray-600 group-hover:text-violet-400 group-hover:translate-x-0.5 transition-all">
                <path d="M5 12h14M12 5l7 7-7 7"/>
              </svg>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Main DemoGate component ──────────────────────────────────────────────────
export default function DemoGate({ children }) {
  // Demo mode activates when:
  // 1. VITE_DEMO_MODE=true is set as env variable (for the demo Vercel project), OR
  // 2. The hostname starts with "demo." (for future custom domain like demo.yourdomain.com)
  const isDemoSubdomain = typeof window !== "undefined" && (
    import.meta.env.VITE_DEMO_MODE === "true" ||
    window.location.hostname.startsWith("demo.")
  );

  const [unlocked,      setUnlocked]      = useState(false);
  const [selectedRole,  setSelectedRole]  = useState(null);
  const [showTutorial,  setShowTutorial]  = useState(false);
  const [tutorialDone,  setTutorialDone]  = useState(false);

  useEffect(() => {
    if (sessionStorage.getItem(DEMO_KEY)) setUnlocked(true);
  }, []);

  // Not demo domain — render normally
  if (!isDemoSubdomain) return children;

  // Password gate
  if (!unlocked) return <PasswordGate onUnlock={() => setUnlocked(true)} />;

  // Role selection
  if (!selectedRole) return <RoleSelector onSelect={(role) => {
    setSelectedRole(role);
    setShowTutorial(true);
  }} />;

  const steps = TUTORIALS[selectedRole] || TUTORIALS.student;

  return (
    <>
      {/* Demo banner */}
      <div
        className="fixed top-0 inset-x-0 z-[999] flex items-center justify-center gap-2 py-1.5 text-[10px] font-black"
        style={{
          background: "linear-gradient(90deg, rgba(139,92,246,0.9), rgba(6,182,212,0.9))",
          boxShadow: "0 2px 12px rgba(139,92,246,0.3)",
        }}
      >
        <span>🎮 DEMO-MODUS</span>
        <span className="opacity-60">·</span>
        <span className="opacity-80">{DEMO_ROLES.find((r) => r.role === selectedRole)?.label}</span>
        <button
          onClick={() => { setSelectedRole(null); setTutorialDone(false); }}
          className="ml-2 px-2 py-0.5 rounded-md bg-white/20 hover:bg-white/30 transition-colors"
        >
          Rolle wechseln
        </button>
        {tutorialDone && (
          <button
            onClick={() => setShowTutorial(true)}
            className="px-2 py-0.5 rounded-md bg-white/20 hover:bg-white/30 transition-colors"
          >
            Tour wiederholen
          </button>
        )}
      </div>

      {/* Push content down to clear banner */}
      <div style={{ paddingTop: 32 }}>
        {children}
      </div>

      {/* Tutorial overlay */}
      {showTutorial && !tutorialDone && (
        <TutorialOverlay
          steps={steps}
          onDone={() => { setShowTutorial(false); setTutorialDone(true); }}
        />
      )}
    </>
  );
}
